// @flow

import { observable, computed, action, IObservableArray } from 'mobx'
import { autobind } from 'core-decorators'
import mousetrap from 'mousetrap'

import { PlayListItem } from './types'

@autobind
export default class PlayQueue {
  audio = new Audio()
  @observable currentTime = 0
  @observable duration = 0
  @observable buffered = 0
  @observable activeIndex = -1
  @observable playlist: IObservableArray<PlayListItem> = []
  @observable isFetching = false
  @observable isLoading = false
  @observable isPlaying = false

  @computed get activeItem(): PlayListItem {
    if (this.activeIndex >= 0 && this.activeIndex < this.playlist.length) {
      return this.playlist[this.activeIndex]
    }
    return null
  }

  constructor() {
    mousetrap.bind('space', this.toggle)
    mousetrap.bind('shift+left', this.playPrev)
    mousetrap.bind('shift+right', this.playNext)

    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', this.play)
      navigator.mediaSession.setActionHandler('pause', this.pause)
      navigator.mediaSession.setActionHandler('previoustrack', this.playPrev)
      navigator.mediaSession.setActionHandler('nexttrack', this.playNext)
    }
  }

  @action onDataLoaded() {
    this.isLoading = false
    this.duration = this.audio.duration
    this.buffered = this.audio.buffered.end(this.audio.buffered.length - 1)
  }

  @action onTimeUpdate() {
    this.currentTime = this.audio.currentTime
  }

  @action onProgress() {
    if (this.audio.buffered.length > 0) {
      this.buffered = this.audio.buffered.end(this.audio.buffered.length - 1)
    }
  }

  @action onEnded() {
    this.playNext()
  }

  @action onError() {
  }

  @action onPlay() {
    this.isPlaying = true
  }

  @action onPause() {
    this.isPlaying = false
  }

  @action play() {
    this.audio.play()
  }

  @action pause() {
    this.audio.pause()
  }

  @action playItemAtIndex(index: number) {
    if (index >= 0 && index < this.playlist.length) {
      const item = this.playlist[index]
      if (item) {
        this.unload()
        this.activeIndex = index

        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: item.track.title,
            artist: item.track.artistName,
            album: item.album.title,
            artwork: [
              { src: item.album.getArtworkURL(94), sizes: '96x96', type: 'image/jpeg' },
              { src: item.album.getArtworkURL(128), sizes: '128x128', type: 'image/jpeg' },
              { src: item.album.getArtworkURL(192), sizes: '192x192', type: 'image/jpeg' },
              { src: item.album.getArtworkURL(256), sizes: '256x256', type: 'image/jpeg' },
              { src: item.album.getArtworkURL(384), sizes: '384x384', type: 'image/jpeg' },
              { src: item.album.getArtworkURL(512), sizes: '512x512', type: 'image/jpeg' },
            ] })
        }

        this.load(item.track.url)
        this.play()
      } else {
        this.pause()
      }
    }
  }

  @action playPrev() {
    this.playItemAtIndex(this.activeIndex - 1)
  }

  @action playNext() {
    this.playItemAtIndex(this.activeIndex + 1)
  }

  @action toggle() {
    if (this.isPlaying) {
      this.pause()
    } else {
      this.play()
    }
    return false
  }

  @action seekTo(value: number) {
    this.audio.currentTime = value
  }

  @action load(src: string) {
    this.isLoading = true
    this.audio.addEventListener('loadeddata', this.onDataLoaded)
    this.audio.addEventListener('timeupdate', this.onTimeUpdate)
    this.audio.addEventListener('progress', this.onProgress)
    this.audio.addEventListener('play', this.onPlay)
    this.audio.addEventListener('pause', this.onPause)
    this.audio.addEventListener('ended', this.onEnded)
    this.audio.addEventListener('error', this.onError)
    this.audio.src = src
  }

  @action unload() {
    this.audio.pause()
    this.audio.removeEventListener('loadeddata', this.onDataLoaded)
    this.audio.removeEventListener('timeupdate', this.onTimeUpdate)
    this.audio.removeEventListener('progress', this.onProgress)
    this.audio.removeEventListener('play', this.onPlay)
    this.audio.removeEventListener('pause', this.onPause)
    this.audio.removeEventListener('ended', this.onEnded)
    this.audio.removeEventListener('error', this.onError)
    this.activeIndex = -1
    this.currentTime = 0
    this.duration = 0
    this.buffered = 0
  }

  @action replace(playlist: Array<PlayListItem>, shouldPlay: boolean = true) {
    this.playlist.replace(playlist)

    if (shouldPlay) {
      this.playItemAtIndex(0)
    }
  }

  @action append(playlist: Array<PlayListItem>) {
    this.playlist.push(...playlist)
  }

  @action setIsFetching(value) {
    this.isFetching = value
  }
}



// WEBPACK FOOTER //
// ./src/stores/play-queue/index.js